import React, { useState } from 'react';
import { NoonReportSectionEnum, NoonReportStatusEnum } from '../../../types/ViewModel/AolEnums';
import { AolDataItem } from '../../../types/ViewModel/AolItem';
import ActivitiesSectionComponent from '../activities-section/ActivitiesSection';
import BridgeSectionComponent from '../bridge-section/BridgeSection';
import EnginesSectionComponent from '../engines-section/EnginesSection';
import FreshWaterSectionComponent from '../fresh-water-section/FreshWaterSection';
import FuelRobSectionComponent from '../fuel-rob-section/FuelRobSection';
import InertGasProductionSectionComponent from '../inert-gas-production/InertGasProductionSection';
import PowerPacksSectionComponent from '../power-packs/PowerPacksSection';
import WeatherSectionComponent from '../weather-section/WeatherSection';
import { ButtonWrapper, Column, MainWrapper, SectionContent, SectionHeaderComponent, SectionWrapper, UnitSelection } from './AolContainer.style';
import { useAuthStore } from '../../../context/AuthContext';
import usePostUpdateAol from '../../../api/usePostUpdateAol';
import { NoonReportSectionData } from '../../../types/ViewModel/NoonReportSectionData';
import { useVesselStore } from '../../../context/VesselContext';
import { useAolStore } from '../../../context/AolContext';
import useAolSaveHandler from '../../../app/useAolSaveHandler';
import SpinnerLoaderComponent from '../../common/loader/SpinnerLoader';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    aolSectionString,
    getAolSectionData,
    getSectionsInColumn,
    hasBeenMrvVerified,
    hasBunkering,
    isSectionVisible,
} from '../../../utils/aolSectionHelper';
import TankCleaningSectionComponent from '../tank-cleaning/TankCleaningSection';
import GeneralSectionComponent from '../general-section/GeneralSection';
import SummarySection from '../summary-section/SummarySection';
import AolValidationMismatchComponent from '../aol-validation-mismatch/AolValidationMismatch';
import CommentsSectionComponent from '../comments-section/CommentsSection';
import { PageType } from '../../../types/Page';
import '../../ui/status-icon/StatusIcon.scss';
import useApiLoaderHelper from '../../../utils/useApiLoaderHelper';
import { SectionHeaderButton } from '../../../styles/button.style';
import CargoTemperatureControlSectionComponent from '../cargo-temperature-control/CargoTemperatureControlSection';
import { handleApproveSection, handleCompleteSection } from '../AolUtils';
import { AppFeatureEnum } from 'src/enums/appFeatureEnum';
import useAppFeature from 'src/app/useAppFeature';

type Props = {
    page: PageType;
    imoNumber: number;
};

const AolContainerComponent: React.FC<Props> = (props) => {
    const { triggerSaveEvent } = useAolSaveHandler();
    const [closedSections] = useState({});
    const {
        selectedAolDataItem,
        selectedUnit,
        setSelectedUnit,
        isSummaryVisible,
        isCommentsVisible,
        selectedAolGroup,
        isEditGloballyMode,
        hasSyncError,
    } = useAolStore();

    const { updateSectionStatus } = usePostUpdateAol();
    const { selectedVessel } = useVesselStore();
    const { role } = useAuthStore();
    const { isLoadingType, isLoadingTypeWithData } = useApiLoaderHelper();
    const { isFeatureEnabled } = useAppFeature();

    const isSavingAolSection = (section: NoonReportSectionEnum) => isLoadingTypeWithData('saving_section', 'section', section);
    const isSavingAol = isLoadingType('saving_aol');

    const isSectionOpen = (section: NoonReportSectionEnum) =>
        !closedSections || closedSections[section] === null || closedSections[section] === undefined;

    const onSaveSectionClicked = (section: NoonReportSectionEnum) => {
        if (!selectedAolDataItem) return;
        triggerSaveEvent('save_section', section, selectedAolDataItem);
        if (!selectedVessel) return;
    };

    const sectionHeaderButtons = (
        sectionData: NoonReportSectionData | undefined,
        section: NoonReportSectionEnum,
        aolStatus: NoonReportStatusEnum
    ) => {
        enum buttonHeaderStatus {
            Save,
            SaveAndComplete,
            Uncomplete,
            RejectAndApprove,
            Unapprove,
            undefined,
        }
        let status;

        if (isEditGloballyMode) {
            status = buttonHeaderStatus.Save;
        } else {
            if (!sectionData || (props.page === 'reporting' && aolStatus === NoonReportStatusEnum.Missing)) {
                status = buttonHeaderStatus.Save;
            } else if (props.page === 'reporting' && !sectionData.isCompleted) {
                status = buttonHeaderStatus.SaveAndComplete;
            } else if (props.page === 'reporting' && sectionData.isCompleted) {
                status = buttonHeaderStatus.Uncomplete;
            } else if (props.page === 'approval' && !sectionData.isApproved) {
                status = buttonHeaderStatus.RejectAndApprove;
            } else if (props.page === 'approval' || (props.page === 'approved' && sectionData.isApproved)) {
                status = buttonHeaderStatus.Unapprove;
            }
        }

        if (hasSyncError && sectionData && sectionData.section && sectionData.section === NoonReportSectionEnum.FuelRob) {
            status = undefined;
        }

        switch (status) {
            case buttonHeaderStatus.Save:
                return (
                    <ButtonWrapper>
                        <SectionHeaderButton disabled={!!isSavingAol} onClick={() => onSaveSectionClicked(section)}>
                            Save
                        </SectionHeaderButton>
                        <span title="Missing section">
                            <FontAwesomeIcon className="status-icon status-unsaved" icon="check-circle" />
                        </span>
                    </ButtonWrapper>
                );
            case buttonHeaderStatus.SaveAndComplete:
                return (
                    <ButtonWrapper>
                        <SectionHeaderButton disabled={!!isSavingAol} onClick={() => onSaveSectionClicked(section)}>
                            Save
                        </SectionHeaderButton>
                        <SectionHeaderButton
                            disabled={!!isSavingAol}
                            onClick={() => {
                                handleCompleteSection(
                                    selectedAolDataItem,
                                    props.imoNumber,
                                    sectionData,
                                    section,
                                    true,
                                    selectedVessel,
                                    updateSectionStatus
                                );
                            }}
                        >
                            Complete
                        </SectionHeaderButton>
                        <span title="Saved section">
                            <FontAwesomeIcon className="status-icon status-saved" icon="check-circle" />
                        </span>
                    </ButtonWrapper>
                );
            case buttonHeaderStatus.Uncomplete:
                return (
                    <ButtonWrapper>
                        <SectionHeaderButton
                            disabled={!!isSavingAol}
                            onClick={() =>
                                handleCompleteSection(
                                    selectedAolDataItem,
                                    props.imoNumber,
                                    sectionData,
                                    section,
                                    false,
                                    selectedVessel,
                                    updateSectionStatus
                                )
                            }
                        >
                            Uncomplete
                        </SectionHeaderButton>
                        <span title="Completed section">
                            <FontAwesomeIcon className="status-icon status-completed" icon="check-circle" />
                        </span>
                    </ButtonWrapper>
                );
            case buttonHeaderStatus.RejectAndApprove:
                return (
                    <ButtonWrapper>
                        <SectionHeaderButton
                            disabled={!!isSavingAol}
                            onClick={() =>
                                handleCompleteSection(
                                    selectedAolDataItem,
                                    props.imoNumber,
                                    sectionData,
                                    section,
                                    false,
                                    selectedVessel,
                                    updateSectionStatus
                                )
                            }
                        >
                            Reject
                        </SectionHeaderButton>
                        <SectionHeaderButton
                            disabled={!!isSavingAol}
                            onClick={() =>
                                handleApproveSection(
                                    selectedAolDataItem,
                                    props.imoNumber,
                                    sectionData,
                                    section,
                                    true,
                                    selectedVessel,
                                    updateSectionStatus
                                )
                            }
                        >
                            Approve
                        </SectionHeaderButton>
                        <span title="Completed section">
                            <FontAwesomeIcon className="status-icon status-completed" icon="check-circle" />
                        </span>
                    </ButtonWrapper>
                );
            case buttonHeaderStatus.Unapprove:
                return (
                    <ButtonWrapper>
                        <SectionHeaderButton
                            disabled={!!isSavingAol}
                            onClick={() =>
                                handleApproveSection(
                                    selectedAolDataItem,
                                    props.imoNumber,
                                    sectionData,
                                    section,
                                    false,
                                    selectedVessel,
                                    updateSectionStatus
                                )
                            }
                        >
                            Reject
                        </SectionHeaderButton>
                        <span title="Approved section">
                            <FontAwesomeIcon className="status-icon status-approved" icon="check-circle" />
                        </span>
                    </ButtonWrapper>
                );
            default:
                return <></>;
        }
    };

    const showColumn = (column: number): boolean => {
        const sections = getSectionsInColumn(column);
        return sections.map((o) => isSectionVisible(o, selectedAolDataItem, selectedVessel)).some((o) => o);
    };

    const getNumberOfColumns = (): number => {
        return (showColumn(1) ? 1 : 0) + (showColumn(2) ? 1 : 0) + (showColumn(3) ? 1 : 0);
    };

    const getFuelRobUnitSelection = (aolDataItem: AolDataItem) => {
        return (
            <>
                {hasBunkering(aolDataItem) && (
                    <UnitSelection>
                        <label>
                            m3
                            <input type="radio" value="m3" name="unitInput" checked={selectedUnit === 'm3'} onChange={() => setSelectedUnit('m3')} />
                        </label>
                        <label>
                            MT
                            <input type="radio" value="MT" name="unitInput" checked={selectedUnit === 'MT'} onChange={() => setSelectedUnit('MT')} />
                        </label>
                    </UnitSelection>
                )}
            </>
        );
    };

    const hasReaderPermissionOnly = () => {
        const isBeforeOrcaWebStartDate = selectedAolGroup?.isBeforeOrcaWebStartDate;
        return (role && !role.isWriter) || isBeforeOrcaWebStartDate;
    };
    const SectionContainer = (section: NoonReportSectionEnum, numberOfColumns?: number) => {
        const SectionSubContainer = (sectionType: NoonReportSectionEnum) => {
            switch (sectionType) {
                case NoonReportSectionEnum.Activities:
                    return <ActivitiesSectionComponent />;
                case NoonReportSectionEnum.Bridge:
                    return <BridgeSectionComponent />;
                case NoonReportSectionEnum.Engines: {
                    const numberOfColumnsInEngineSection = numberOfColumns === 3 ? 1 : 2;
                    return <EnginesSectionComponent numberOfColumns={numberOfColumnsInEngineSection} />;
                }
                case NoonReportSectionEnum.FreshWater:
                    return <FreshWaterSectionComponent />;
                case NoonReportSectionEnum.FuelRob:
                    return <FuelRobSectionComponent />;
                case NoonReportSectionEnum.General:
                    return <GeneralSectionComponent />;
                case NoonReportSectionEnum.Comments:
                    return <CommentsSectionComponent />;
                case NoonReportSectionEnum.InertGasProduction:
                    return <InertGasProductionSectionComponent />;
                case NoonReportSectionEnum.PowerPacks:
                    return <PowerPacksSectionComponent />;
                case NoonReportSectionEnum.TankCleaning:
                    return <TankCleaningSectionComponent />;
                case NoonReportSectionEnum.CargoTemperatureControl:
                    return <CargoTemperatureControlSectionComponent />;
                case NoonReportSectionEnum.Weather:
                    return <WeatherSectionComponent />;
            }
        };

        const SectionComponent = () => {
            if (!selectedAolDataItem || !isSectionVisible(section, selectedAolDataItem, selectedVessel)) return <></>;

            const sectionData = getAolSectionData(selectedAolDataItem.noonReport, section);
            return (
                <>
                    <SectionHeaderComponent>
                        {aolSectionString(section)}
                        {section === NoonReportSectionEnum.FuelRob && getFuelRobUnitSelection(selectedAolDataItem)}
                        {!hasReaderPermissionOnly() &&
                            !hasBeenMrvVerified(selectedVessel?.mrvVerifiedUntilDate, selectedAolGroup?.aolDate) &&
                            sectionHeaderButtons(sectionData, section, selectedAolDataItem.status)}
                    </SectionHeaderComponent>
                    <SectionContent isVisible={isSectionOpen(section)}>{SectionSubContainer(section)}</SectionContent>
                </>
            );
        };

        return (
            <>
                {isSavingAolSection(section) && <SpinnerLoaderComponent message={'Saving...'} showContainerOverlay={true} clearDefaultText={true} />}
                {SectionComponent()}
            </>
        );
    };

    const numberOfColumns = getNumberOfColumns();

    return (
        <MainWrapper numberOfColumns={numberOfColumns}>
            {selectedAolDataItem && (
                <>
                    {selectedAolDataItem.aolValidationItems && (
                        <SectionWrapper gridCol={1} gridColSpan={numberOfColumns}>
                            <AolValidationMismatchComponent aolValidationItems={selectedAolDataItem.aolValidationItems} />
                        </SectionWrapper>
                    )}
                    <>
                        {showColumn(1) && (
                            <Column>
                                <SectionWrapper gridCol={1} gridColSpan={1}>
                                    {SectionContainer(NoonReportSectionEnum.Engines, numberOfColumns)}
                                </SectionWrapper>
                            </Column>
                        )}
                        {showColumn(2) && (
                            <Column>
                                <SectionWrapper gridCol={1} gridColSpan={2}>
                                    {SectionContainer(NoonReportSectionEnum.TankCleaning)}
                                </SectionWrapper>
                                <SectionWrapper gridCol={1} gridColSpan={2}>
                                    {SectionContainer(NoonReportSectionEnum.CargoTemperatureControl)}
                                </SectionWrapper>
                                {!isFeatureEnabled({
                                    featureEnum: AppFeatureEnum.Bunkering,
                                    date: selectedAolDataItem.reportDate,
                                    imoNumber: props.imoNumber,
                                }) && (
                                    <SectionWrapper gridCol={1} gridColSpan={2}>
                                        {SectionContainer(NoonReportSectionEnum.FuelRob)}
                                    </SectionWrapper>
                                )}
                                <SectionWrapper gridCol={1} gridColSpan={2}>
                                    {SectionContainer(NoonReportSectionEnum.FreshWater)}
                                </SectionWrapper>
                            </Column>
                        )}
                        {showColumn(3) && (
                            <Column>
                                {isSummaryVisible && (
                                    <SectionWrapper>
                                        <SummarySection key={`${selectedAolGroup?.aolDate}_summary`} />
                                    </SectionWrapper>
                                )}
                                <SectionWrapper>{SectionContainer(NoonReportSectionEnum.General)}</SectionWrapper>
                                {isCommentsVisible && <SectionWrapper>{SectionContainer(NoonReportSectionEnum.Comments)}</SectionWrapper>}
                                <SectionWrapper>{SectionContainer(NoonReportSectionEnum.Activities)}</SectionWrapper>
                                <SectionWrapper>{SectionContainer(NoonReportSectionEnum.Bridge)}</SectionWrapper>
                                <SectionWrapper>{SectionContainer(NoonReportSectionEnum.Weather)}</SectionWrapper>
                                <SectionWrapper>{SectionContainer(NoonReportSectionEnum.InertGasProduction)}</SectionWrapper>
                                <SectionWrapper>{SectionContainer(NoonReportSectionEnum.PowerPacks)}</SectionWrapper>
                            </Column>
                        )}
                    </>
                </>
            )}
        </MainWrapper>
    );
};

export default AolContainerComponent;
